import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { hoursFormatter, numberFormatter } from "@/lib/report-metrics";
import type { ManagerReportSiteBreakdownEntry } from "@/types/reports";

type SiteBreakdownTableProps = {
  rows: ManagerReportSiteBreakdownEntry[];
  isLoading?: boolean;
  emptyLabel?: string;
  limit?: number;
};

function formatLastActivity(value: string | null) {
  if (!value) return "No activity";

  return formatDistanceToNow(new Date(value), {
    addSuffix: true,
  });
}

export function SiteBreakdownTable({
  rows,
  isLoading,
  emptyLabel = "No sign-ins recorded for these filters.",
  limit,
}: SiteBreakdownTableProps) {
  const displayRows = typeof limit === "number" ? rows.slice(0, limit) : rows;

  if (isLoading) {
    return <p>Loading site activity…</p>;
  }

  if (displayRows?.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No sign-ins recorded</EmptyTitle>
          <EmptyDescription>{emptyLabel}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="min-w-[100px] sm:min-w-[140px]">Site</TableHead>
          <TableHead className="text-center min-w-[60px] sm:min-w-[80px]">
            Sign-ins
          </TableHead>
          <TableHead className="text-center min-w-[60px] sm:min-w-[80px]">
            Workers
          </TableHead>
          <TableHead className="text-center min-w-[60px] sm:min-w-[80px]">
            Flagged
          </TableHead>
          <TableHead className="text-center min-w-[70px] sm:min-w-[100px]">
            Avg shift
          </TableHead>
          <TableHead className="text-center min-w-[80px] sm:min-w-[120px]">
            Last activity
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {displayRows.map((site) => (
          <TableRow key={site.siteId}>
            <TableCell className="font-medium">
              <Link to={`/sites/${site.siteId}`} className="underline">
                {site.siteName}
              </Link>
            </TableCell>
            <TableCell className="text-center">
              {numberFormatter.format(site.signInCount)}
            </TableCell>
            <TableCell className="text-center">
              {numberFormatter.format(site.uniqueWorkers)}
            </TableCell>
            <TableCell className="text-center">
              {numberFormatter.format(site.flaggedSignIns)}
            </TableCell>
            <TableCell className="text-center">
              {hoursFormatter.format(site.averageShiftHours)} h
            </TableCell>
            <TableCell className="text-center text-muted-foreground">
              {formatLastActivity(site.lastSignInTime)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
